import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, LeaderboardEntry, SalespersonMetrics } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface ObjectionHandlingByRepChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface RepObjectionScore {
  name: string;
  score: number;
}

const ObjectionHandlingByRepChart: React.FC<ObjectionHandlingByRepChartProps> = ({
  businessIdentifier,
  timeRange = '7d',
}) => {
  const [repScores, setRepScores] = useState<RepObjectionScore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const leaderboard = await analyticsService.getSalesLeaderboard(
          businessIdentifier,
          timeRange,
          'total_conversations',
          15
        );
        const entries = leaderboard.leaderboard || [];

        if (entries.length === 0) {
          setRepScores([]);
          return;
        }

        const metricsResults = await Promise.allSettled(
          entries.map((entry) => analyticsService.getSalespersonMetrics(entry.salesperson_id))
        );

        const scores: RepObjectionScore[] = [];
        metricsResults.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            scores.push({
              name: entries[idx].salesperson_name,
              score: result.value.objection_handling_score || 0,
            });
          }
        });

        // Sort by score descending
        scores.sort((a, b) => b.score - a.score);
        setRepScores(scores);
      } catch (err: any) {
        console.error('[ObjectionHandlingByRepChart] Error fetching data:', err);
        setError(err.message || 'Failed to load objection handling data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (repScores.length === 0) return {};

    const names = repScores.map((r) => r.name);
    const scores = repScores.map((r) => r.score);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const rep = repScores[idx];
          if (!rep) return '';
          return `<b>${rep.name}</b><br/>Objection Handling: ${rep.score.toFixed(1)}`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          rotate: repScores.length > 6 ? 30 : 0,
          width: 70,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: 'Score',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'bar',
          data: scores.map((score) => ({
            value: score,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: score >= 70 ? CHART_COLORS.positive : score >= 40 ? CHART_COLORS.orange : CHART_COLORS.negative },
                  { offset: 1, color: (score >= 70 ? CHART_COLORS.positive : score >= 40 ? CHART_COLORS.orange : CHART_COLORS.negative) + '77' },
                ],
              },
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'top',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            formatter: (params: any) => params.value?.toFixed(1),
          },
        },
      ],
    };
  };

  return (
    <ChartCard
      title="Objection Handling by Rep"
      subtitle="Objection handling effectiveness score per salesperson"
      isLoading={isLoading}
      error={error}
    >
      {repScores.length === 0 && !isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '300px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default ObjectionHandlingByRepChart;
